import {
  ActivityIcon,
  ActivityTimeline,
  Button,
  DocumentIcon,
  EmptyState,
  ErrorState,
  MeetingIcon,
  OpportunityIcon,
  Spinner,
  type SuggestedLink,
  SuggestedLinksSection,
  type TimelineEntry,
} from '@olytic/foundation';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import {
  decideActivitySuggestion,
  type LinkDecision,
  LinkSuggestionError,
  loadActivitySuggestions,
  toSuggestedLink,
} from '../activity/linkSuggestions';
import { type ActivityItem, loadActivity } from '../opportunities/opportunityPageApi';
import { formatDate } from './format';
import { isObjectKey, recordPath } from './routes';

const PAGE = 50;

/** What an empty timeline says will show there, by object. */
const EMPTY_DESCRIPTION = {
  opportunities: 'Meetings, emails and stage changes show here.',
  contacts: 'Meetings and emails show here.',
  accounts: 'Meetings and emails show here.',
  // A lead has touches only (leadPage.mjs): no meeting links.
  leads: 'Texts, calls and emails show here.',
  // A project's meetings and the touches linked to it (projectPage.mjs).
  projects: 'Meetings, texts, calls and emails show here.',
} as const;

function iconOf(type: string): JSX.Element {
  if (type === 'meeting') return <MeetingIcon />;
  if (type === 'stage') return <OpportunityIcon />;
  if (type.startsWith('quote')) return <DocumentIcon />;
  return <ActivityIcon />;
}

/** What a touch's Suggested Links say the AI read (the default names a meeting's transcript). */
const TOUCH_SUGGESTIONS_NOTE = 'Found by AI from this email, text or call. Accept adds the link; Dismiss removes the suggestion.';

/** A timeline item, which for a touch with suggestions waiting carries how many (MOTHER-286). */
type TimelineItem = ActivityItem & { suggestion_count?: number };

const ACTIVITY_PREFIX = 'activity:';

/** The touch's activity id, when the item is a touch with suggested links waiting for a person. */
function touchWithSuggestions(item: TimelineItem): { activityId: string; count: number } | null {
  const count = item.suggestion_count;
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 1) return null;
  if (!item.id.startsWith(ACTIVITY_PREFIX) || item.id.length === ACTIVITY_PREFIX.length) return null;
  return { activityId: item.id.slice(ACTIVITY_PREFIX.length), count };
}

const messageOf = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

/**
 * A touch's AI-suggested links (MOTHER-288), behind a "Review Suggested Links (N)" button so a
 * timeline makes no request for them until a person asks. Accept adds the link, so the timeline
 * is read again (`onLinked`) to show it; Dismiss only drops the row and the count. A 409 means
 * someone already decided it: the row goes, with no error. Who may decide is the route's call,
 * so Accept and Dismiss always show and a refusal shows its error.
 */
function TouchSuggestions({ activityId, count, onLinked }: { activityId: string; count: number; onLinked: () => void }): JSX.Element | null {
  const [left, setLeft] = useState(count);
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [suggestions, setSuggestions] = useState<SuggestedLink[]>([]);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  // A reread timeline brings the server's count.
  useEffect(() => setLeft(count), [count]);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Loaded once; only a failed load is asked again.
    if (load === 'loading' || load === 'ready') return;
    setLoad('loading');
    setError(null);
    loadActivitySuggestions(activityId)
      .then((answer) => {
        if (!live.current) return;
        setSuggestions(answer.suggestions.map(toSuggestedLink));
        setLoad('ready');
      })
      .catch((e: unknown) => {
        if (!live.current) return;
        setError(messageOf(e, 'The suggested links could not be loaded.'));
        setLoad('error');
      });
  };

  const decide = (suggestion: SuggestedLink, decision: LinkDecision) => {
    setError(null);
    setBusyIds((busy) => [...busy, suggestion.id]);
    void decideActivitySuggestion(activityId, suggestion.id, decision)
      .then(() => ({ done: true, reload: decision === 'accept' }))
      .catch((e: unknown) => {
        if (e instanceof LinkSuggestionError && e.status === 409) return { done: true, reload: true };
        if (live.current) setError(messageOf(e, 'The suggestion could not be saved.'));
        return { done: false, reload: false };
      })
      .then(({ done, reload }) => {
        if (!live.current) return;
        setBusyIds((busy) => busy.filter((id) => id !== suggestion.id));
        if (!done) return;
        setSuggestions((list) => list.filter((s) => s.id !== suggestion.id));
        setLeft((n) => Math.max(0, n - 1));
        if (reload) onLinked();
      });
  };

  if (left === 0 && !(open && (suggestions.length > 0 || error))) return null;
  return (
    <div className="mt-2 flex flex-col items-start gap-2">
      {left > 0 ? (
        <Button variant="link" size="sm" className="h-auto p-0" aria-expanded={open} loading={load === 'loading'} onClick={toggle}>
          Review Suggested Links ({left})
        </Button>
      ) : null}
      {open && load !== 'idle' && load !== 'loading' ? (
        <SuggestedLinksSection
          suggestions={suggestions}
          onAccept={(s) => decide(s, 'accept')}
          onDismiss={(s) => decide(s, 'dismiss')}
          busyIds={busyIds}
          error={error}
          canDecide
          note={TOUCH_SUGGESTIONS_NOTE}
        />
      ) : null}
    </div>
  );
}

/**
 * One item as a timeline entry, its title a link to the record when it has one. A touch with
 * suggested links waiting adds their button and section under its detail (`onLinked` rereads
 * the timeline once one is accepted).
 */
export function entryOf(item: TimelineItem, onNavigate: (href: string) => void, onLinked: () => void = () => undefined): TimelineEntry {
  const href = item.link && isObjectKey(item.link.object) ? recordPath(item.link.object, item.link.id) : null;
  const detail = item.detail && item.detail !== item.actor ? item.detail : null;
  const touch = touchWithSuggestions(item);
  const body: ReactNode = touch ? (
    <>
      {detail}
      <TouchSuggestions activityId={touch.activityId} count={touch.count} onLinked={onLinked} />
    </>
  ) : detail;
  return {
    id: item.id,
    title: href ? (
      <Button variant="link" size="sm" className="h-auto p-0" onClick={() => onNavigate(href)}>
        {item.title}
      </Button>
    ) : (
      item.title
    ),
    ...(body ? { body } : {}),
    ...(item.actor ? { actor: item.actor } : {}),
    timestamp: formatDate(item.at),
    icon: iconOf(item.type),
    ...(item.type === 'stage' ? { tone: 'accent' as const } : {}),
  };
}

/**
 * The Activity tab of an opportunity (meetings, emails, logged activity, stage
 * changes and quotes), a contact or an account (meetings, emails and logged
 * activity), a lead (its texts, calls and emails; a lead has no meetings), or a project (its
 * meetings and the touches linked to it), newest first.
 */
export function ActivityTab({ object, recordId, onNavigate }: {
  object: 'opportunities' | 'contacts' | 'accounts' | 'leads' | 'projects';
  recordId: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const [limit, setLimit] = useState(PAGE);
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; items: ActivityItem[]; total: number }>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    loadActivity(object, recordId, limit)
      .then((page) => { if (live) setLoad({ state: 'ready', items: page.items, total: page.total }); })
      .catch((e: unknown) => { if (live) setLoad({ state: 'error', message: e instanceof Error ? e.message : 'Could not load.' }); });
    return () => { live = false; };
  }, [object, recordId, limit, attempt]);

  if (load.state === 'loading') return <Spinner label="Loading activity…" />;
  if (load.state === 'error') return <ErrorState title="Could not load activity" description={load.message} onRetry={() => setAttempt((n) => n + 1)} />;
  if (load.items.length === 0) return <EmptyState title="No activity yet" description={EMPTY_DESCRIPTION[object]} />;

  return (
    <div className="flex flex-col gap-3">
      <ActivityTimeline datePlacement="leading" entries={load.items.map((item) => entryOf(item, onNavigate, () => setAttempt((n) => n + 1)))} />
      {load.total > load.items.length && limit < 100 ? (
        <div>
          <Button variant="neutral" size="sm" onClick={() => setLimit((n) => Math.min(n + PAGE, 100))}>
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
